import { useCallback, useEffect, useMemo, useState } from "react";
import localStorageConstants from "src/app/constants/localStorage.constants";
import { RequestManagementUser } from "src/app/models/screens/requestManagement.model";
import {
  TaskRequest,
  TaskRequestStatus,
} from "src/app/models/screens/task.model";
import {
  acceptTaskRequest,
  getParentChildren,
  getReceivedTaskRequests,
  getSentTaskRequests,
  getUserTags,
  rejectTaskRequest,
  respondToMoreTimeRequest,
  searchUsers,
} from "src/app/utils/backendEndpointCalls.util";

interface ParentChild {
  _id: string;
  firstName: string;
}

const TaskRequestsScreenViewModel = () => {
  const [isSnackbarError, setIsSnackbarError] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [isDisplayingSnackbar, setIsDisplayingSnackbar] =
    useState<boolean>(false);

  const [userRole, setUserRole] = useState<string | undefined>(undefined);
  const [children, setChildren] = useState<ParentChild[]>([]);
  const [isLoadingChildren, setIsLoadingChildren] = useState<boolean>(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>("you");
  const [adminRequestUsers, setAdminRequestUsers] = useState<
    RequestManagementUser[]
  >([]);
  const [isLoadingAdminUsers, setIsLoadingAdminUsers] =
    useState<boolean>(false);

  const [selectedTab, setSelectedTab] = useState<number>(0);

  const [receivedRequests, setReceivedRequests] = useState<TaskRequest[]>([]);
  const [isLoadingReceived, setIsLoadingReceived] = useState<boolean>(false);

  const [sentRequests, setSentRequests] = useState<TaskRequest[]>([]);
  const [isLoadingSent, setIsLoadingSent] = useState<boolean>(false);

  const [isViewingAsAdmin, setIsViewingAsAdmin] = useState<boolean>(false);
  const [tags, setTags] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (userRole === "Admin" && selectedOwnerId !== "you")
      setIsViewingAsAdmin(true);
    else setIsViewingAsAdmin(false);
  }, [userRole, selectedOwnerId]);

  const getCurrentChildId = useCallback((): string | undefined => {
    if (userRole !== "Parent") return undefined;
    return selectedOwnerId === "you" ? undefined : selectedOwnerId;
  }, [selectedOwnerId, userRole]);

  const getCurrentAdminUserId = useCallback((): string | undefined => {
    if (userRole !== "Admin") return undefined;
    return selectedOwnerId === "you" ? undefined : selectedOwnerId;
  }, [selectedOwnerId, userRole]);

  const showError = (message: string) => {
    setIsSnackbarError(true);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  const showSuccess = (message: string) => {
    setIsSnackbarError(false);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  const getUserTagsAsync = async (childId?: string, adminUserId?: string) => {
    const getTagsResponse = await getUserTags(childId ?? adminUserId);
    if (!getTagsResponse.success) return;
    const nextTags = new Map<string, string>();
    Object.keys(getTagsResponse.tags ?? {}).forEach((key: string) => {
      nextTags.set(key, getTagsResponse.tags[key]);
    });
    setTags(nextTags);
  };

  const minutesToLabel = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}`;
  };

  const getStatusChipColor = (
    status: TaskRequestStatus,
  ): "warning" | "success" | "error" => {
    if (status === "Accepted") return "success";
    if (status === "Rejected") return "error";
    return "warning";
  };
  const loadReceivedRequests = useCallback(async () => {
    setIsLoadingReceived(true);
    const response = await getReceivedTaskRequests(
      getCurrentChildId(),
      getCurrentAdminUserId(),
    );
    setIsLoadingReceived(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    const sorted = [...(response.requests ?? [])].sort(sortRequests);
    setReceivedRequests(sorted);
  }, [getCurrentAdminUserId, getCurrentChildId]);

  const loadSentRequests = useCallback(async () => {
    setIsLoadingSent(true);
    const response = await getSentTaskRequests(
      getCurrentChildId(),
      getCurrentAdminUserId(),
    );
    setIsLoadingSent(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setSentRequests(response.requests ?? []);
  }, [getCurrentAdminUserId, getCurrentChildId]);

  const loadParentChildren = useCallback(async () => {
    setIsLoadingChildren(true);
    const response = await getParentChildren();
    setIsLoadingChildren(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setChildren(response.children ?? []);
  }, []);

  const loadAdminUsers = useCallback(async () => {
    setIsLoadingAdminUsers(true);
    const response = await searchUsers("");
    setIsLoadingAdminUsers(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setAdminRequestUsers(response.users ?? []);
  }, []);

  useEffect(() => {
    const savedRole = localStorage.getItem(localStorageConstants.ROLE);
    if (!savedRole) return;
    setUserRole(savedRole);
  }, []);

  useEffect(() => {
    if (userRole === "Parent") {
      loadParentChildren();
    }
    if (userRole === "Admin") {
      loadAdminUsers();
    }
  }, [loadAdminUsers, loadParentChildren, userRole]);

  useEffect(() => {
    if (!userRole) return;
    if (selectedTab === 0) {
      loadReceivedRequests();
    } else {
      loadSentRequests();
    }
  }, [
    userRole,
    selectedTab,
    selectedOwnerId,
    loadReceivedRequests,
    loadSentRequests,
  ]);

  useEffect(() => {
    if (!userRole) return;
    getUserTagsAsync(getCurrentChildId(), getCurrentAdminUserId());
  }, [getCurrentAdminUserId, getCurrentChildId, selectedOwnerId, userRole]);

  const sortRequests = (a: TaskRequest, b: TaskRequest): number => {
    if (a.status === "Pending" && b.status !== "Pending") return -1;
    if (a.status !== "Pending" && b.status === "Pending") return 1;
    return 0;
  };

  const onTabChanged = (_event: React.SyntheticEvent, value: number) => {
    setSelectedTab(value);
  };

  const onOwnerChanged = (ownerId: string) => {
    setSelectedOwnerId(ownerId);
    setReceivedRequests([]);
    setSentRequests([]);
  };

  const onAcceptRequest = async (requestId: string) => {
    if (isViewingAsAdmin) return;
    const response = await acceptTaskRequest(requestId, getCurrentChildId());
    if (!response.success) {
      showError(response.error);
      return;
    }
    setReceivedRequests((prev) =>
      prev.map((r) => (r.id === requestId ? { ...r, status: "Accepted" } : r)),
    );
    showSuccess("Task request accepted.");
  };

  const onRejectRequest = async (requestId: string) => {
    if (isViewingAsAdmin) return;
    const response = await rejectTaskRequest(requestId, getCurrentChildId());
    if (!response.success) {
      showError(response.error);
      return;
    }
    setReceivedRequests((prev) =>
      prev.map((r) => (r.id === requestId ? { ...r, status: "Rejected" } : r)),
    );
    showSuccess("Task request rejected.");
  };

  const onAcceptMoreTime = async (requestId: string) => {
    if (isViewingAsAdmin) return;
    const response = await respondToMoreTimeRequest(
      requestId,
      true,
      getCurrentChildId(),
    );
    if (!response.success) {
      showError(response.error);
      return;
    }
    setReceivedRequests((prev) =>
      prev.map((r) =>
        r.id === requestId ? { ...r, moreTimeStatus: "Accepted" } : r,
      ),
    );
    showSuccess("Reschedule request accepted.");
  };

  const onRejectMoreTime = async (requestId: string) => {
    if (isViewingAsAdmin) return;
    const response = await respondToMoreTimeRequest(
      requestId,
      false,
      getCurrentChildId(),
    );
    if (!response.success) {
      showError(response.error);
      return;
    }
    setReceivedRequests((prev) =>
      prev.map((r) =>
        r.id === requestId ? { ...r, moreTimeStatus: "Rejected" } : r,
      ),
    );
    showSuccess("Reschedule request rejected.");
  };

  const canActOnRequests = useMemo(() => {
    return !isViewingAsAdmin;
  }, [isViewingAsAdmin]);

  return {
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    userRole,
    children,
    isLoadingChildren,
    selectedOwnerId,
    onOwnerChanged,
    adminRequestUsers,
    isLoadingAdminUsers,
    selectedTab,
    onTabChanged,
    receivedRequests,
    isLoadingReceived,
    sentRequests,
    isLoadingSent,
    minutesToLabel,
    getStatusChipColor,
    onAcceptRequest,
    onRejectRequest,
    onAcceptMoreTime,
    onRejectMoreTime,
    canActOnRequests,
    isViewingAsAdmin,
    tags,
  };
};

export default TaskRequestsScreenViewModel;
